import { useEffect, useState } from "react";
import { claimsApi } from "@/api/endpoints";
import type { CommandCenterClaimCard, CommandCenterResponse } from "@/api/types";
import { useAuth } from "@/auth/AuthContext";
import { canAccess } from "@/auth/permissions";
import { ClaimQueueTable } from "@/components/claims/ClaimQueueTable";
import { NewClaimModal } from "@/components/creates/NewClaimModal";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { formatCurrency } from "@/utils/format";

const QUEUE_SECTIONS: Array<{ title: string; key: keyof CommandCenterResponse }> = [
  { title: "Needs immediate review", key: "needs_review" },
  { title: "My assignments", key: "my_assignments" },
  { title: "Fraud alerts", key: "fraud_alerts" },
  { title: "SLA breaches & warnings", key: "sla_breaches" },
  { title: "Incoming (24h)", key: "incoming" },
  { title: "Unclaimed", key: "unclaimed" },
  { title: "High severity", key: "high_severity" },
];

function isClaimCardList(value: unknown): value is CommandCenterClaimCard[] {
  return Array.isArray(value);
}

export function ClaimsPage() {
  const { user } = useAuth();
  const [data, setData] = useState<CommandCenterResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    claimsApi
      .commandCenter()
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load claims"));
  }, []);

  if (error) return <div className="error-banner">{error}</div>;
  if (!data) {
    return (
      <div className="loading-screen" style={{ minHeight: "12rem" }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Claims"
        subtitle="Command center for triage, fraud, SLA, and review."
        actions={
          canAccess(user, "claims.view") ? (
            <button type="button" className="btn btn-primary" onClick={() => setShowCreate(true)}>
              + New claim (FNOL)
            </button>
          ) : null
        }
      />
      <NewClaimModal open={showCreate} onClose={() => setShowCreate(false)} />
      <section className="stat-grid">
        <StatCard label="Open claims" value={data.kpis.open_claims} to="/claims" />
        <StatCard label="Review queue" value={data.kpis.human_review_queue} to="/claims" />
        <StatCard label="My assignments" value={data.kpis.my_assignments} to="/claims" />
        <StatCard label="SLA at risk" value={data.kpis.sla_at_risk + data.kpis.sla_breached} to="/claims" />
        <StatCard label="Fraud alerts" value={data.kpis.fraud_alerts} to="/claims" />
        <StatCard
          label="Open exposure"
          value={formatCurrency(data.kpis.open_exposure_cents)}
          to="/claims"
        />
      </section>

      <div style={{ display: "grid", gap: "1rem" }}>
        {QUEUE_SECTIONS.map((section) => {
          const items = data[section.key];
          if (!isClaimCardList(items)) return null;
          return <ClaimQueueTable key={section.key} title={section.title} items={items} />;
        })}
      </div>
    </>
  );
}
